import { RequestStatusEnum, TransferTypeEnum, requestApis } from "@payping/api";
import { Delete, FailedDollar, PhRPlus, UploadOutlined } from "@payping/icons";
import { setCashOutRequestList } from "@payping/redux/actions";
import {
  Alert,
  Button,
  Col,
  DataHeader,
  Drawer,
  LegacyForm,
  Modal,
  Row,
  Space,
  Tag,
  Typography,
  Upload,
  useMessagedAsync,
  useMessagedAsyncFn,
  usePaypingContext,
} from "@payping/web-components";
import React, { Dispatch, useCallback, useState } from "react";
import type { UploadChangeParam } from "@payping/web-components/components/upload/types";
import type { FormInstance } from "@payping/web-components/legacyComponents/Form/types";
import { useAsync } from "react-use";
import { useParams } from "react-router-dom";
import HandlingCrispInMobile from "@/components/mobile/HandlingCrispInMobile";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { useTransferLabel } from "@/utils/useTransferLabel";
import { deleteAllErrorsId, uploadDraggerId } from "@/assets/googleTrackIds";
import { generateExcel } from "@/utils/excelGenerator";
import { DropdownButton } from "@/core/component";
import ManualRequest from "../manualRequest";
import List from "./List";
import BulkRequest from "../firstStep/BulkRequest";
import ManualAddition from "../manualAddition";
import { SettlementConfiguration } from "../index";

interface Props {
  next: () => void;
  formStepOne: FormInstance<SettlementConfiguration>;
  formStepTwo: FormInstance<SettlementRowType>;
  setFileUploaded: Dispatch<React.SetStateAction<boolean>>;
  setLoadingStepTwo: React.Dispatch<React.SetStateAction<boolean>>;
  setLoadingDelete: React.Dispatch<React.SetStateAction<boolean>>;
  setRequestTitle: React.Dispatch<React.SetStateAction<string>>;
  setRequestDescription: React.Dispatch<React.SetStateAction<string>>;
  setTransferType: React.Dispatch<React.SetStateAction<TransferTypeEnum>>;
  setRequestStatus: React.Dispatch<React.SetStateAction<RequestStatusEnum>>;
  requestTitle: string;
  transferType: TransferTypeEnum;
}

export interface SettlementRowType {
  clientRefId?: string;
  code: string;
  iban?: string;
  fullName?: string;
  amount?: string;
  description?: string;
  error?: string;
  tableIdCol: number;
  mobile?: string;
  nationalId?: string;
  settlement: any[];
}

const SecondStep: React.FC<Props> = ({
  formStepOne,
  formStepTwo,
  setFileUploaded,
  next,
  setLoadingStepTwo,
  setLoadingDelete,
  setTransferType,
  setRequestDescription,
  setRequestTitle,
  setRequestStatus,
  transferType,
  requestTitle,
}) => {
  const { code } = useParams<{ code: string }>() as { code: string };
  const { t } = useAppTranslation();

  const dispatch = useAppDispatch();
  const [errorText, setErrorText] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [modal, contextHolder] = Modal.useModal();
  const { createdRequest } = useAppSelector((state) => state.cashOuts);
  const { getLabelTransfer } = useTransferLabel();
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const { breakpoints } = usePaypingContext();
  const { lg } = breakpoints;

  const { loading: loadingList } = useAsync(async () => {
    // const responseData = await requestPublicApis.requestsListWithQueryGet(
    //   "1",
    //   undefined,
    //   undefined,
    //   undefined,
    //   `code=${code}`
    // );

    const responseData = await requestApis.getRequestDetailsWithRequestCodeWithCancellationTokenGet(code);

    setRequestTitle(responseData.title || "");
    setRequestDescription(responseData.description || "");
    setTransferType(responseData.transferType || TransferTypeEnum.AUTO);
    setRequestStatus(responseData.status || RequestStatusEnum.Incomplete);

    await dispatch(setCashOutRequestList({ code, settlements: responseData.settlements }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const [{ loading: loadingDelete }, handleDelete] = useMessagedAsyncFn(
    async (goNext?: boolean) => {
      setLoadingDelete(true);
      modal.confirm({
        cancelText: t("cancel"),
        content: t("message.remove", { subject: t("withdraw.withdraw") }),
        mask: { closable: true },
        okText: t("ok"),
        onOk: async () => {
          if (goNext) {
            next();
          }
        },
      });
      setLoadingDelete(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, createdRequest?.errors, createdRequest?.settlements],
    undefined,
    { useProgress: true },
  );

  const [{ loading }, onFinish] = useMessagedAsyncFn(
    async () => {
      setLoadingStepTwo(true);
      if (createdRequest) {
        if (!(createdRequest.errors && createdRequest.errors.length > 0)) {
          next();
        } else await handleDelete(true);
      }
      setLoadingStepTwo(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createdRequest, next],
    undefined,
    {
      useProgress: true,
    },
  );

  const [, downloadErrors] = useMessagedAsyncFn(async () => {
    if (createdRequest?.errors) {
      const excelData = createdRequest?.errors.map((data) => {
        const { amount, error, clientRefId, iban, description } = data;
        return [amount, error, clientRefId, iban, description];
      });
      generateExcel([
        [t("settlement.amount"), t("status"), t("settlement.clientRefId"), t("shaba"), t("description")],
        [...excelData],
      ]);
    }
  }, [createdRequest?.errors, t]);

  const [{ loading: bulkLoading }, requestFunction] = useMessagedAsyncFn(
    async (file) => {
      const values = formStepOne.getFieldsValue();
      const transType = values.transferType ? values.transferType : TransferTypeEnum.AUTO;
      try {
        const response = await requestApis.createBatchRequestWithExcelWithFileWithModelPost(
          requestTitle,
          file,
          values.description,
          undefined,
          undefined,
          transType,
          {
            isRaw: true,
          },
        );
        setFileUploaded(true);
        await dispatch(setCashOutRequestList(response));
        return JSON.stringify(response);
      } catch (error: any) {
        if (error.status >= 500) {
          setErrorText(t("settlement.error.serverError"));
          setModalVisible(true);
        } else {
          const contentType = error.headers.get("content-type");
          const errorMsg = contentType.includes("application/json") ? await error.json() : "";
          setErrorText(errorMsg.error);
          setModalVisible(true);
        }
        return error;
      }
    },
    [dispatch, formStepOne, requestTitle, setFileUploaded, t],
    undefined,
    {
      useProgress: true,
    },
  );

  const onChange = async (info: UploadChangeParam) => {
    await formStepOne.validateFields();
    const { status } = info.file;
    if (status === "uploading") {
      setFileUploaded(true);
    } else if (status === "done") {
      setFileUploaded(false);
    } else setFileUploaded(false);
  };

  const callApi = async (options: any) => {
    await formStepOne.validateFields();
    const { file, onSuccess, onError } = options;
    requestFunction(file)
      .then(async (result) => {
        onSuccess?.(result);
      })
      .catch(onError);
    return {
      abort() {},
    };
  };

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const getData = useCallback(async () => {
    if (createdRequest?.errors && createdRequest?.settlements)
      return [...createdRequest.errors, ...createdRequest.settlements];
    if (createdRequest?.errors) return [createdRequest.errors];
    if (createdRequest?.settlements) return [...createdRequest.settlements];
    return [];
  }, [createdRequest?.errors, createdRequest?.settlements]);

  const buttonItems = [
    {
      key: 1,
      label: t("settlement.downloadErrorList"),
      icon: <UploadOutlined width={14} />,
    },
  ];

  useMessagedAsync(
    async () => {
      const data = await getData();
      formStepTwo.setFieldValue?.("settlement", data);
    },
    [getData, formStepTwo],
    {
      success: false,
      useProgress: true,
    },
  );
  return (
    <LegacyForm<SettlementRowType> onFinish={onFinish} form={formStepTwo} className="tw:w-full" layout={"vertical"}>
      <Row gutter={[0, 16]} className={!lg ? "tw:mb-50! tw:w-full" : undefined}>
        {lg ? null : (
          <LegacyForm.Item noStyle>
            <Col xs={24}>
              <Space>
                <Typography.Text>{requestTitle}</Typography.Text>
                <Tag>{getLabelTransfer(transferType)}</Tag>
              </Space>
            </Col>
          </LegacyForm.Item>
        )}

        {lg ? (
          <LegacyForm.Item noStyle dependencies={["settlement"]}>
            {({ getFieldValue }) =>
              getFieldValue("settlement")?.length > 0 ? null : (
                <Col xs={24}>
                  <BulkRequest form={formStepOne} requestFunction={requestFunction} uploadLoading={bulkLoading} />
                </Col>
              )
            }
          </LegacyForm.Item>
        ) : null}
        <Modal footer={null} open={modalVisible} width={700} centered onCancel={() => setModalVisible(false)}>
          <div className="tw:px-4! tw:pt-8! tw:text-center">
            <FailedDollar width={100} height={100} className="tw:mx-auto tw:mb-8! tw:block" />
            <Typography.Text strong className="font-rem-14 tw:my-12! tw:block">
              {errorText}
            </Typography.Text>
            <Row justify="space-around" className="tw:mt-12!">
              <Button onClick={() => setModalVisible(false)} className="tw:w-full" size="large" type={"primary"}>
                {t("tryAgain")}
              </Button>
            </Row>
          </div>
        </Modal>
        <Col xs={24}>
          <DataHeader
            primaryAction={
              lg ? (
                <DropdownButton
                  menu={{ items: buttonItems, onClick: downloadErrors }}
                  key={1}
                  buttonIcon={<Delete width={14} height={14} />}
                  disabled={!((createdRequest?.errors && createdRequest?.errors?.length > 0) || loadingDelete)}
                  onClick={() => handleDelete()}
                  data-google-track={deleteAllErrorsId}
                >
                  {t("deleteAllErrors")}
                </DropdownButton>
              ) : (
                <Upload
                  disabled={loading || !formStepOne.getFieldValue("title")}
                  onChange={onChange}
                  customRequest={callApi}
                  allowedExtensions={["xls", "xlsx"]}
                  showUploadList={true}
                  maxCount={1}
                  data-google-track={uploadDraggerId}
                  crop={false}
                >
                  <Button icon={<UploadOutlined width={14} height={14} />}>{t("settlement.create.uploadFile")}</Button>
                </Upload>
              )
            }
          />
        </Col>
        <LegacyForm.Item noStyle dependencies={["settlement"]}>
          {({ getFieldValue }) => {
            const rowValues = getFieldValue("settlement") || [];
            const hasEmptyFullName = rowValues.some((item: any) => !item.fullName?.trim());

            return hasEmptyFullName ? (
              <Col xs={24}>
                <Alert
                  type="warning"
                  title={t("settlement.create.unspecifiedTransferDestination")}
                  showIcon
                  description={t("settlement.create.unclearInquiryDesc")}
                />
              </Col>
            ) : null;
          }}
        </LegacyForm.Item>
        <LegacyForm.List name={"settlement"}>
          {(fields) => (
            <>
              <Col xs={24}>
                <List loading={loadingList} fields={fields} form={formStepTwo} />
              </Col>
            </>
          )}
        </LegacyForm.List>
        <Col xs={24}>
          {lg ? (
            <ManualRequest loadingList={loadingList} form={formStepTwo} />
          ) : (
            <>
              <Button
                className={"tw:w-full"}
                block
                icon={<PhRPlus width={14} height={14} />}
                onClick={() => setOpenDrawer(true)}
              >
                {t("add.add")}
              </Button>
              <Drawer closable={false} mask={false} open={openDrawer} placement={"bottom"} size={"100%"}>
                <HandlingCrispInMobile>
                  <ManualAddition loadingList={loadingList} form={formStepTwo} setOpenDrawer={setOpenDrawer} />
                </HandlingCrispInMobile>
              </Drawer>
            </>
          )}
        </Col>
        {contextHolder}
      </Row>
    </LegacyForm>
  );
};

export default SecondStep;
